// Story 8.12 (ADO-2021, D-6) — the per-SKU "already on order" cache behind the on-order chip.
//
// Populated by batched reads from the three surfaces that show the chip (browse, product detail,
// the order review). Per-SKU and batch-loaded like the availability store, with three
// properties that follow from the data:
//
// - It is Customer-wide, not per depot (AC2), so a depot switch does NOT reset it; a change of
//   signed-in user does, before the read, so one user never sees another's orders.
// - It must change after a send (`invalidateAll`), so a product just sent shows as on order. A
//   send carries every contributor's lines, not only the caller's, so the whole cache is read
//   again rather than a guessed SKU list. A per-SKU request stamp makes the newest read win, so
//   an older read still in flight when the send lands cannot overwrite the fresher answer.
// - Other Shoppers of the Customer order too (AC2), so an answer older than
//   `ON_ORDER_CACHE_TTL_MS` is read again the next time a surface asks for it (`useOnOrder` on
//   mount or on a changed SKU set); a page left open does not poll. The old answer keeps
//   rendering until the fresh one lands.
//
// A SKU the read could not answer (`unreadSkus`: its chunk failed or drifted) never reads as
// "not on order": with no answer, or a `'none'`, it goes back to unknown (no chip); with an
// on-order answer, that answer stays, as an advisory chip should, and if it was expired the next
// load asks again.

import { create } from 'zustand';
import { getOnOrder, type OnOrderProduct } from '../api/on-order.client';

/** A product on order, or `'none'` when the server answered that it is not. */
export type OnOrderAnswer = OnOrderProduct | 'none';

interface OnOrderState {
  /** Absent = not answered yet (render nothing). */
  readonly bySku: Readonly<Record<string, OnOrderAnswer>>;
  /** The signed-in user `bySku` belongs to; a change resets the cache. */
  readonly userKey: string | null;
  /** Read the SKUs not answered yet (or answered too long ago) and not in flight, for `userKey`.
   *  Never rejects. */
  loadForSkus: (userKey: string, skuCodes: readonly string[]) => Promise<void>;
  /** Read these SKUs again for the current user, superseding any older read. Never rejects. */
  invalidate: (skuCodes: readonly string[]) => Promise<void>;
  /** Read again every SKU this cache knows (answered, unread or in flight), superseding any older
   *  read. After a send that may have placed an order. Never rejects. */
  invalidateAll: () => Promise<void>;
  /** Drop all state (sign-out, tests). */
  reset: () => void;
}

/**
 * How long an answer is trusted before the next load reads it again (5 minutes, interim: no
 * product figure exists). Long enough that browsing does not re-ask on every page, short enough
 * that a colleague's order, or an ETA that has passed, shows on the next page that asks.
 */
export const ON_ORDER_CACHE_TTL_MS = 5 * 60 * 1000;

// Bumped by `reset()` and a user change; a read commits only when the stamp is unchanged.
let generation = 0;
// The newest request id issued per SKU, and the ids still in flight. A read commits a SKU only
// when its id is still that SKU's newest, which is what lets `invalidate` win over a stale read.
let requestSequence = 0;
const latestRequestBySku = new Map<string, number>();
const inFlight = new Set<number>();
// When each committed answer arrived (epoch ms), for the TTL.
const answeredAtBySku = new Map<string, number>();

/** True when the answer for `sku` arrived at least `ON_ORDER_CACHE_TTL_MS` ago. */
function isExpired(sku: string): boolean {
  const answeredAt = answeredAtBySku.get(sku);
  return answeredAt !== undefined && Date.now() - answeredAt >= ON_ORDER_CACHE_TTL_MS;
}

function forgetRequests(): void {
  latestRequestBySku.clear();
  answeredAtBySku.clear();
}

export const useOnOrderStore = create<OnOrderState>((set, get) => {
  async function read(skus: readonly string[]): Promise<void> {
    if (skus.length === 0) return;
    const gen = generation;
    requestSequence += 1;
    const requestId = requestSequence;
    for (const sku of skus) latestRequestBySku.set(sku, requestId);
    inFlight.add(requestId);
    // The SKUs this request still answers for: a newer request supersedes it per SKU.
    const owned = (): string[] => skus.filter((sku) => latestRequestBySku.get(sku) === requestId);
    try {
      const { products, unreadSkus } = await getOnOrder(skus);
      if (gen !== generation) return;
      if (unreadSkus.length > 0) {
        console.warn(
          `On-order read could not answer ${unreadSkus.length} SKU(s); they stay unknown`,
        );
      }
      const unread = new Set(unreadSkus);
      const answered = new Map(products.map((onOrder) => [onOrder.skuCode, onOrder]));
      const merged: Record<string, OnOrderAnswer> = { ...get().bySku };
      const answeredAt = Date.now();
      for (const sku of owned()) {
        if (!unread.has(sku)) {
          merged[sku] = answered.get(sku) ?? 'none';
          answeredAtBySku.set(sku, answeredAt);
        } else if (merged[sku] === undefined || merged[sku] === 'none') {
          // "Could not ask" is not "not on order": back to unknown.
          delete merged[sku];
          answeredAtBySku.delete(sku);
        }
        // An unread SKU holding an on-order answer keeps it, with its old timestamp.
      }
      set({ bySku: merged });
    } finally {
      inFlight.delete(requestId);
    }
  }

  return {
    bySku: {},
    userKey: null,

    async loadForSkus(userKey, skuCodes) {
      if (userKey === '') return;
      if (get().userKey !== userKey) {
        generation += 1;
        forgetRequests();
        set({ bySku: {}, userKey });
      }
      const { bySku } = get();
      const wanted = [...new Set(skuCodes)].filter((sku) => {
        if (sku === '') return false;
        if (bySku[sku] !== undefined && !isExpired(sku)) return false;
        const pending = latestRequestBySku.get(sku);
        return pending === undefined || !inFlight.has(pending);
      });
      await read(wanted);
    },

    async invalidate(skuCodes) {
      if (get().userKey === null) return;
      await read([...new Set(skuCodes)].filter((sku) => sku !== ''));
    },

    async invalidateAll() {
      // Every SKU asked about since the last reset, plus any answer set without a request.
      await get().invalidate([...latestRequestBySku.keys(), ...Object.keys(get().bySku)]);
    },

    reset() {
      generation += 1;
      forgetRequests();
      set({ bySku: {}, userKey: null });
    },
  };
});
